"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Sha256 } from "@aws-crypto/sha256-browser";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  FileUp,
  Link2,
  LoaderCircle,
  ShieldCheck,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import MiroFishGraphPanel from "@/components/mirofish/graph-panel";
import MiroFishSiteFooter from "@/components/mirofish/site-footer";
import MiroFishSiteHeader from "@/components/mirofish/site-header";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppContext } from "@/contexts/app";
import {
  getLotteryPredictionProjectPath,
  LOTTERY_PREDICTION_PATH,
  lotteryMethodCards,
  lotteryPredictionFaq,
  lotteryWorkflowSteps,
} from "@/content/lottery-prediction";
import { WORLD_CUP_PREDICTION_PATH } from "@/content/world-cup-prediction";
import { Link } from "@/i18n/navigation";
import { isAuthEnabled } from "@/lib/auth";
import { trackMiroFishStartEngineGate } from "@/lib/mirofish-analytics";
import { createMiroFishClientSubmissionId } from "@/lib/mirofish-api";
import type {
  LotteryAnalysisResult,
  LotteryAnalysisState,
} from "@/types/mirofish-lottery";

type ApiError = { error?: string; error_code?: string; errorCode?: string };
type SourceMode = "upload" | "link";

const DEFAULT_PROMPT = `Act as an evidence-focused Lotto 6/49 research analyst. Use only uploaded draws that occurred before the forecast date stated in these instructions. If no forecast date is stated, use the current date as the analysis cutoff.

1. Compare number frequency and omission behavior across the full history, the most recent 100 draws, and the most recent 30 draws.
2. Evaluate odd/even balance, low/high balance, decade coverage, sums, spans, consecutive numbers, repeated numbers, co-occurrence, and next-draw relationships.
3. Run three independent perspectives: frequency and recency, structural balance, and omission and transitions. Let them challenge one another before producing a consensus ranking.
4. Run a Monte Carlo simulation with 250,000 iterations using the consensus scores.
5. Return a complete 01–49 ranking, a 15-number pool, a Bottom 10 descriptive group, and three six-number coverage scenarios named Stable, Balanced, and Exploratory.

For every result, distinguish historical description from forward-looking inference, state uncertainty clearly, disclose the 1 in 13,983,816 exact-set odds, and never imply a guaranteed win.`;
const LEGACY_LATEST_PROJECT_STORAGE_KEY = "mirofish.lottery.latestProjectId";
const AUTO_RUN_PROJECT_STORAGE_KEY = "mirofish.lottery.autoRunProjectId";

const readAutoRunProjectReference = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.sessionStorage.getItem(AUTO_RUN_PROJECT_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
};

const persistAutoRunProjectReference = (projectId: string) => {
  if (typeof window === "undefined" || !projectId) return;
  try {
    window.sessionStorage.setItem(AUTO_RUN_PROJECT_STORAGE_KEY, projectId);
  } catch {
    // The active in-memory workflow still continues when session storage is unavailable.
  }
};

const clearAutoRunProjectReference = (projectId: string) => {
  if (typeof window === "undefined" || !projectId) return;
  try {
    if (window.sessionStorage.getItem(AUTO_RUN_PROJECT_STORAGE_KEY) === projectId) {
      window.sessionStorage.removeItem(AUTO_RUN_PROJECT_STORAGE_KEY);
    }
  } catch {
    // Ignore unavailable session storage after the workflow has already advanced.
  }
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatExecutionParameter = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item || typeof item !== "object") return String(item);
        const row = item as { label?: unknown; draws?: unknown; weight?: unknown };
        const draws = row.draws === null ? "all" : String(row.draws ?? "");
        return `${String(row.label || "window")}: ${draws} draws × ${String(row.weight ?? 1)}`;
      })
      .join(" · ");
  }
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const sha256File = async (file: File) => {
  const hasher = new Sha256();
  hasher.update(new Uint8Array(await file.arrayBuffer()));
  const digest = await hasher.digest();
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
};

const validateSourceUrl = (value: string) => {
  try {
    return new URL(value.trim()).protocol === "https:";
  } catch {
    return false;
  }
};

const readJson = async <T,>(response: Response): Promise<T> => {
  const payload = (await response.json().catch(() => ({}))) as T & ApiError;
  if (!response.ok) {
    throw new Error(payload.error || `Request failed with status ${response.status}.`);
  }
  return payload;
};

const NumberBall = ({ number, active }: { number: number; active?: boolean }) => (
  <span
    className={`flex aspect-square items-center justify-center rounded-full border font-mono text-[10px] tabular-nums transition sm:text-xs ${
      active
        ? "border-white/90 bg-white text-black shadow-[0_0_28px_rgba(255,255,255,0.18)]"
        : "border-white/10 bg-black/20 text-white/45"
    }`}
  >
    {String(number).padStart(2, "0")}
  </span>
);

export default function LotteryPredictionPage({
  projectId: routeProjectId,
}: {
  projectId?: string;
}) {
  const router = useRouter();
  const { user, setShowSignModal, setUser } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sourceMode, setSourceMode] = useState<SourceMode>("upload");
  const [sourceUrl, setSourceUrl] = useState("");
  const [linkError, setLinkError] = useState("");
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [project, setProject] = useState<LotteryAnalysisState | null>(null);
  const [result, setResult] = useState<LotteryAnalysisResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [clientReady, setClientReady] = useState(false);
  const [restoringProject, setRestoringProject] = useState(Boolean(routeProjectId));
  const [error, setError] = useState("");
  const [autoRunProjectId, setAutoRunProjectId] = useState("");
  const restoredProjectRef = useRef("");
  const startRequestProjectRef = useRef("");
  const submissionRef = useRef<{ key: string; id: string } | null>(null);

  const activeNumbers = useMemo(
    () => new Set(result?.scenarios[1]?.combination || []),
    [result]
  );
  const workflowActive = Boolean(
    project &&
      ["validating", "awaiting_confirmation", "confirming", "queued", "analyzing"].includes(
        project.status
      )
  );
  const authEnabled = isAuthEnabled();
  const isSignedIn = authEnabled ? Boolean(user?.uuid || user?.email) : true;
  const hasValidSource =
    sourceMode === "upload"
      ? Boolean(file)
      : validateSourceUrl(sourceUrl) && !linkError;
  const canSubmit = hasValidSource && prompt.trim().length > 0;
  const requiresFilledInputs =
    !authEnabled || (isSignedIn && user?.hasActiveSubscription === true);
  const startEngineDisabled =
    !clientReady ||
    isCheckingAccess ||
    busy ||
    workflowActive ||
    (requiresFilledInputs ? !canSubmit : false);
  const startEngineLabel =
    authEnabled && !isSignedIn
      ? "Sign in to start engine"
      : isCheckingAccess
        ? "Checking access..."
        : "Start Engine";

  useEffect(() => {
    setClientReady(true);
  }, []);

  useEffect(() => {
    const projectId = routeProjectId?.trim() || "";
    if (!projectId) {
      const leavingBoundProject = Boolean(restoredProjectRef.current);
      restoredProjectRef.current = "";
      if (leavingBoundProject) {
        startRequestProjectRef.current = "";
        setFile(null);
        setSourceMode("upload");
        setSourceUrl("");
        setLinkError("");
        setPrompt(DEFAULT_PROMPT);
        setProject(null);
        setResult(null);
        setBusy(false);
        setError("");
        setAutoRunProjectId("");
        submissionRef.current = null;
      }
      setRestoringProject(false);
      try {
        window.localStorage.removeItem(LEGACY_LATEST_PROJECT_STORAGE_KEY);
      } catch {
        // Ignore unavailable storage; project restoration no longer depends on it.
      }
      const cleanUrl = new URL(window.location.href);
      if (cleanUrl.searchParams.has("projectId")) {
        cleanUrl.searchParams.delete("projectId");
        window.history.replaceState(
          window.history.state,
          "",
          `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`
        );
      }
      return;
    }
    if (restoredProjectRef.current === projectId) return;
    restoredProjectRef.current = projectId;
    setProject(null);
    setResult(null);
    setError("");
    setRestoringProject(true);
    let cancelled = false;
    const restore = async () => {
      try {
        const response = await fetch(`/api/mirofish/lottery/projects/${encodeURIComponent(projectId)}`, {
          cache: "no-store",
        });
        const payload = await readJson<{ project: LotteryAnalysisState }>(response);
        if (cancelled) return;
        const pendingAutoRunProjectId = readAutoRunProjectReference();
        if (
          pendingAutoRunProjectId === projectId &&
          ["validating", "awaiting_confirmation"].includes(payload.project.status)
        ) {
          setAutoRunProjectId(projectId);
        } else if (!["validating", "awaiting_confirmation"].includes(payload.project.status)) {
          clearAutoRunProjectReference(projectId);
        }
        setProject(payload.project);
        if (payload.project.prompt) setPrompt(payload.project.prompt);
        if (payload.project.analysisId && payload.project.status === "report_ready") {
          const analysisResponse = await fetch(
            `/api/mirofish/lottery/analyses/${encodeURIComponent(payload.project.analysisId)}`,
            { cache: "no-store" }
          );
          const analysisPayload = await readJson<{ analysis: LotteryAnalysisState }>(analysisResponse);
          if (!cancelled) {
            setProject(analysisPayload.analysis);
            setResult(analysisPayload.analysis.result || null);
            setAutoRunProjectId("");
            clearAutoRunProjectReference(projectId);
          }
        }
      } catch (restoreError) {
        if (!cancelled) {
          setError(restoreError instanceof Error ? restoreError.message : "Could not restore lottery analysis.");
        }
      } finally {
        if (!cancelled) setRestoringProject(false);
      }
    };
    restore();
    return () => {
      cancelled = true;
    };
  }, [routeProjectId]);

  useEffect(() => {
    if (!project || !["validating"].includes(project.status)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`/api/mirofish/lottery/projects/${project.projectId}`, {
          cache: "no-store",
        });
        const payload = await readJson<{ project: LotteryAnalysisState }>(response);
        if (!cancelled) {
          setProject(payload.project);
          if (payload.project.status === "failed") {
            setAutoRunProjectId("");
            clearAutoRunProjectReference(payload.project.projectId);
          }
        }
      } catch (pollError) {
        if (!cancelled) setError(pollError instanceof Error ? pollError.message : "Validation polling failed.");
      }
    };
    poll();
    const timer = window.setInterval(poll, 1800);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [project?.projectId, project?.status]);

  useEffect(() => {
    if (
      !project?.datasetHash ||
      project.status !== "awaiting_confirmation" ||
      autoRunProjectId !== project.projectId ||
      startRequestProjectRef.current === project.projectId
    ) {
      return;
    }

    let cancelled = false;
    startRequestProjectRef.current = project.projectId;
    const startAnalysis = async () => {
      setBusy(true);
      setError("");
      try {
        const payload = await readJson<{ project: LotteryAnalysisState }>(
          await fetch(`/api/mirofish/lottery/projects/${project.projectId}/confirm`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ datasetHash: project.datasetHash }),
          })
        );
        if (!cancelled) {
          setProject(payload.project);
          setAutoRunProjectId("");
          clearAutoRunProjectReference(project.projectId);
        }
      } catch (startError) {
        if (!cancelled) {
          setAutoRunProjectId("");
          clearAutoRunProjectReference(project.projectId);
          setError(startError instanceof Error ? startError.message : "Analysis could not start.");
        }
      } finally {
        startRequestProjectRef.current = "";
        if (!cancelled) setBusy(false);
      }
    };
    startAnalysis();
    return () => {
      cancelled = true;
    };
  }, [autoRunProjectId, project?.datasetHash, project?.projectId, project?.status]);

  useEffect(() => {
    if (!project?.analysisId || !["queued", "analyzing"].includes(project.status)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`/api/mirofish/lottery/analyses/${project.analysisId}`, {
          cache: "no-store",
        });
        const payload = await readJson<{ analysis: LotteryAnalysisState }>(response);
        if (cancelled) return;
        setProject(payload.analysis);
        if (payload.analysis.result) setResult(payload.analysis.result);
      } catch (pollError) {
        if (!cancelled) setError(pollError instanceof Error ? pollError.message : "Analysis polling failed.");
      }
    };
    poll();
    const timer = window.setInterval(poll, 1800);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [project?.analysisId, project?.status]);

  const redirectToPricing = () => {
    router.push(
      `/pricing?intent=start-engine&return_to=${encodeURIComponent(LOTTERY_PREDICTION_PATH)}`
    );
  };

  const resolveStartEngineAccess = async () => {
    if (!authEnabled) {
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "allowed",
      });
      return "allowed" as const;
    }

    if (!isSignedIn) {
      setShowSignModal(true);
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "sign_in",
      });
      return "sign_in" as const;
    }

    if (user?.hasActiveSubscription === true) {
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "allowed",
      });
      return "allowed" as const;
    }

    if (user?.hasActiveSubscription === false) {
      redirectToPricing();
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "pricing",
      });
      return "pricing" as const;
    }

    setIsCheckingAccess(true);
    try {
      const response = await fetch("/api/get-user-info", { method: "POST" });
      const payload = await response.json().catch(() => null);

      if (response.status === 401 || payload?.code === -2) {
        setShowSignModal(true);
        trackMiroFishStartEngineGate({
          source: "engine_console",
          result: "sign_in",
        });
        return "sign_in" as const;
      }

      if (!response.ok || payload?.code !== 0 || !payload?.data) {
        setError(
          typeof payload?.message === "string" && payload.message.trim()
            ? payload.message
            : "Unable to verify your subscription. Please try again."
        );
        trackMiroFishStartEngineGate({
          source: "engine_console",
          result: "error",
        });
        return "error" as const;
      }

      setUser(payload.data);
      if (payload.data.hasActiveSubscription === true) {
        trackMiroFishStartEngineGate({
          source: "engine_console",
          result: "allowed",
        });
        return "allowed" as const;
      }

      redirectToPricing();
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "pricing",
      });
      return "pricing" as const;
    } catch {
      setError("Unable to verify your subscription. Please try again.");
      trackMiroFishStartEngineGate({
        source: "engine_console",
        result: "error",
      });
      return "error" as const;
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const validateDataset = async () => {
    if (isCheckingAccess || busy || workflowActive) {
      return;
    }

    setError("");
    const access = await resolveStartEngineAccess();
    if (access !== "allowed") {
      return;
    }

    if (sourceMode === "upload" && !file) {
      setError("Choose a PDF, TXT, MD, or Markdown source file first.");
      return;
    }
    if (sourceMode === "link" && !validateSourceUrl(sourceUrl)) {
      setLinkError("Enter a valid public HTTPS link.");
      return;
    }
    setBusy(true);
    setError("");
    setLinkError("");
    const pendingProjectId = readAutoRunProjectReference();
    if (pendingProjectId) clearAutoRunProjectReference(pendingProjectId);
    setAutoRunProjectId("");
    setProject(null);
    setResult(null);
    try {
      const sourceKey =
        sourceMode === "link"
          ? sourceUrl.trim()
          : file
            ? `${file.name}:${file.size}:${file.lastModified}`
            : "";
      const submissionKey = `${sourceMode}\n${sourceKey}\n${prompt.trim()}`;
      if (submissionRef.current?.key !== submissionKey) {
        submissionRef.current = {
          key: submissionKey,
          id: `lottery_${createMiroFishClientSubmissionId()}`,
        };
      }
      const clientSubmissionId = submissionRef.current?.id;
      if (!clientSubmissionId) {
        throw new Error("Could not prepare this analysis request.");
      }
      let uploadId: string;

      if (sourceMode === "link") {
        const imported = await readJson<{
          upload: {
            uploadId: string;
          };
        }>(
          await fetch("/api/mirofish/lottery/import-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientSubmissionId,
              url: sourceUrl.trim(),
            }),
          })
        );
        uploadId = imported.upload.uploadId;
      } else {
        const selectedFile = file as File;
        const fileId = createMiroFishClientSubmissionId();
        const sha256 = await sha256File(selectedFile);
        const init = await readJson<{
          uploads: Array<{
            uploadId: string;
            uploadUrl: string;
            headers: Record<string, string>;
          }>;
        }>(
          await fetch("/api/mirofish/uploads/init", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientSubmissionId,
              files: [
                {
                  fileId,
                  name: selectedFile.name,
                  size: selectedFile.size,
                  contentType:
                    selectedFile.type || "application/octet-stream",
                  sha256,
                },
              ],
            }),
          })
        );
        const upload = init.uploads[0];
        let directUploadSucceeded = false;
        try {
          const uploadResponse = await fetch(upload.uploadUrl, {
            method: "PUT",
            headers: upload.headers,
            body: selectedFile,
          });
          directUploadSucceeded = uploadResponse.ok;
        } catch {
          // Regional object-storage errors are retried through the same-origin server.
        }
        if (!directUploadSucceeded) {
          await readJson(
            await fetch(
              `/api/mirofish/uploads/${encodeURIComponent(upload.uploadId)}/content`,
              {
                method: "PUT",
                headers: {
                  "Content-Type":
                    selectedFile.type || "application/octet-stream",
                  "x-mirofish-client-submission-id": clientSubmissionId,
                },
                body: selectedFile,
              }
            )
          );
        }
        await readJson(
          await fetch("/api/mirofish/uploads/complete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientSubmissionId,
              uploadIds: [upload.uploadId],
            }),
          })
        );
        uploadId = upload.uploadId;
      }

      const created = await readJson<{ project: LotteryAnalysisState }>(
        await fetch("/api/mirofish/lottery/projects", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientSubmissionId,
            uploadIds: [uploadId],
            prompt,
          }),
        })
      );
      persistAutoRunProjectReference(created.project.projectId);
      setAutoRunProjectId(created.project.projectId);
      setProject(created.project);
      router.replace(getLotteryPredictionProjectPath(created.project.projectId));
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Dataset validation failed.");
    } finally {
      setBusy(false);
    }
  };

  const resumeAnalysis = () => {
    if (!project?.datasetHash || project.status !== "awaiting_confirmation") return;
    setError("");
    startRequestProjectRef.current = "";
    persistAutoRunProjectReference(project.projectId);
    setAutoRunProjectId(project.projectId);
  };

  return (
    <div className="min-h-screen bg-[#04050a] text-white">
      <MiroFishSiteHeader hashHrefPrefix="/" />
      <main id="main-content" className="overflow-hidden">
        <section className="relative overflow-hidden border-b border-white/10 px-4 pb-16 pt-14 sm:px-6 lg:px-8 lg:pb-20 lg:pt-18">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-[8%] top-[-10%] h-[420px] w-[420px] rounded-full bg-sky-500/20 blur-[120px]" />
            <div className="absolute right-[6%] top-[2%] h-[420px] w-[420px] rounded-full bg-orange-500/16 blur-[120px]" />
            <div className="absolute bottom-[-45%] left-1/2 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-violet-500/12 blur-[140px]" />
          </div>
          <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-xs uppercase tracking-[0.24em] text-white/65">
                <Sparkles className="h-4 w-4 text-amber-300" />
                Lotto 6/49 · evidence lab
              </div>
              <h1 className="mt-6 max-w-3xl text-balance text-4xl font-semibold leading-[1.02] tracking-[-0.045em] text-white sm:text-6xl lg:text-7xl">
                Analyze Lotto 6/49 history.
                <span className="block text-white/55">Evidence, not superstition.</span>
              </h1>
              <p className="mt-7 max-w-2xl text-base leading-8 text-white/62 sm:text-lg">
                Add a 6/49 draw-history file or public link, verify every record, and get a transparent statistical report with backtests, number rankings, and three coverage scenarios—without claims of guaranteed winning numbers.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-[0.16em] text-white/52">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-2">Default: Taiwan Lotto 6/49</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-2">Backtest-gated rankings</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-white/70">Exact-set odds disclosed</span>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-10 rounded-full bg-orange-500/10 blur-3xl" />
              <div className="relative rounded-[36px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-5 shadow-[0_20px_120px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-7">
                <div className="mb-5 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.24em] text-white/40">49-number field</span>
                  <span className="text-xs text-white/36">Balanced scenario highlights after analysis</span>
                </div>
                <div className="grid grid-cols-7 gap-2 sm:gap-2.5">
                  {Array.from({ length: 49 }, (_, index) => (
                    <NumberBall key={index + 1} number={index + 1} active={activeNumbers.has(index + 1)} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.84fr_1.16fr]">
              <div className="rounded-[32px] border border-white/10 bg-white/5 p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-sky-300">
                    <UploadCloud className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-white/40">
                      {routeProjectId ? "Project evidence" : "Evidence input"}
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-[-0.025em]">
                      {routeProjectId ? "Saved Lotto 6/49 analysis" : "Add Lotto 6/49 draw history"}
                    </h2>
                  </div>
                </div>

                {routeProjectId ? (
                  <div className="mt-8 space-y-5">
                    <div className="rounded-2xl border border-white/[0.08] bg-black/25 p-4">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                        Project ID
                      </p>
                      <p className="mt-2 break-all font-mono text-xs text-sky-100/70">
                        {routeProjectId}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-white/66">Source documents</p>
                      <div className="mt-2 space-y-2">
                        {project?.sourceDocuments.length ? (
                          project.sourceDocuments.map((document) => (
                            <div
                              key={document.uploadId}
                              className="rounded-2xl border border-white/[0.08] bg-black/25 p-4"
                            >
                              <p className="break-words text-sm font-semibold text-white/82">
                                {document.filename}
                              </p>
                              <p className="mt-2 text-xs text-white/38">
                                {formatBytes(document.size)} · {document.contentType}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="rounded-2xl border border-white/[0.08] bg-black/25 p-4 text-sm text-white/38">
                            {restoringProject ? "Loading project evidence…" : "No source document metadata is available."}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-white/66">Analysis cutoff</p>
                      <p className="mt-2 rounded-2xl border border-white/[0.08] bg-black/25 px-4 py-3 font-mono text-sm text-white/75">
                        {project?.effectiveTargetDate || project?.targetDrawDate || "Determined from the prompt"}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-white/66">Analysis instructions</p>
                      <div className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-[24px] border border-white/[0.08] bg-black/25 px-4 py-3 text-sm leading-6 text-white/62">
                        {project?.prompt || prompt}
                      </div>
                    </div>

                    <Link
                      href={LOTTERY_PREDICTION_PATH}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/[0.06] px-5 py-3.5 text-sm font-semibold text-white/82 transition hover:bg-white/[0.1]"
                    >
                      Start a new analysis
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                ) : (
                  <>
                    <Tabs
                      value={sourceMode}
                      onValueChange={(value) => {
                        const nextMode = value as SourceMode;
                        setSourceMode(nextMode);
                        setError("");
                        setLinkError(
                          nextMode === "link" &&
                            sourceUrl.trim() &&
                            !validateSourceUrl(sourceUrl)
                            ? "Enter a valid public HTTPS link."
                            : ""
                        );
                      }}
                      className="mt-8 gap-4"
                    >
                      <TabsList className="grid h-11 w-full grid-cols-2 rounded-lg border border-white/10 bg-black/30 p-1 text-white/55">
                        <TabsTrigger
                          value="upload"
                          className="rounded-md text-sm text-white/55 data-[state=active]:bg-white/12 data-[state=active]:text-white"
                          data-testid="lottery-source-upload-tab"
                        >
                          <FileUp className="h-4 w-4" />
                          Upload File
                        </TabsTrigger>
                        <TabsTrigger
                          value="link"
                          className="rounded-md text-sm text-white/55 data-[state=active]:bg-white/12 data-[state=active]:text-white"
                          data-testid="lottery-source-link-tab"
                        >
                          <Link2 className="h-4 w-4" />
                          Import Link
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="upload" className="mt-0">
                        <button
                          type="button"
                          disabled={!clientReady}
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={(event) => {
                            event.preventDefault();
                            setFile(event.dataTransfer.files?.[0] || null);
                            setError("");
                          }}
                          className="flex w-full flex-col items-center justify-center rounded-[28px] border border-dashed border-white/15 bg-black/30 px-6 py-9 text-center transition hover:border-white/30 hover:bg-black/35 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          <FileUp className="h-8 w-8 text-sky-300" />
                          <span className="mt-4 text-base font-medium text-white">
                            {file
                              ? file.name
                              : "Drag a draw-history file here or click to browse"}
                          </span>
                          <span className="mt-2 max-w-sm text-sm leading-6 text-white/50">
                            {file
                              ? `${formatBytes(file.size)} · ${file.type || "unknown MIME"}`
                              : "PDF, MD, Markdown, or TXT, up to 50MB."}
                          </span>
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          disabled={!clientReady}
                          accept=".pdf,.txt,.md,.markdown,application/pdf,text/plain,text/markdown"
                          aria-label="Upload Lotto 6/49 draw history"
                          data-testid="lottery-file-input"
                          className="hidden"
                          onChange={(event) => {
                            setFile(event.target.files?.[0] || null);
                            setError("");
                            event.currentTarget.value = "";
                          }}
                        />
                      </TabsContent>

                      <TabsContent value="link" className="mt-0">
                        <div className="rounded-[28px] border border-white/10 bg-black/30 p-5">
                          <label
                            htmlFor="lottery-source-url"
                            className="text-sm font-medium text-white"
                          >
                            Public file link
                          </label>
                          <div className="relative mt-3">
                            <Link2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                            <Input
                              id="lottery-source-url"
                              type="url"
                              inputMode="url"
                              value={sourceUrl}
                              onChange={(event) => {
                                setSourceUrl(event.target.value);
                                setLinkError("");
                                setError("");
                              }}
                              onBlur={() => {
                                if (
                                  sourceUrl.trim() &&
                                  !validateSourceUrl(sourceUrl)
                                ) {
                                  setLinkError(
                                    "Enter a valid public HTTPS link."
                                  );
                                }
                              }}
                              placeholder="https://drive.google.com/... or any public HTTPS file"
                              aria-invalid={Boolean(linkError)}
                              data-testid="lottery-source-url"
                              className="h-12 rounded-lg border-white/10 bg-white/5 pl-11 text-white placeholder:text-white/30"
                            />
                          </div>
                          <p className="mt-3 text-sm leading-6 text-white/50">
                            Google Drive, Dropbox, OneDrive, or a direct PDF,
                            MD, Markdown, or TXT link. Maximum 10MB.
                          </p>
                          {linkError ? (
                            <p
                              role="alert"
                              className="mt-3 text-sm text-rose-200/80"
                            >
                              {linkError}
                            </p>
                          ) : null}
                        </div>
                      </TabsContent>
                    </Tabs>

                    <label className="mt-6 block text-sm text-white/66">
                      What should the report analyze?
                      <textarea
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        rows={6}
                        className="mt-2 w-full resize-y rounded-[24px] border border-white/10 bg-black/35 px-4 py-3 text-sm leading-6 text-white/82 outline-none transition focus:border-white/30"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={startEngineDisabled}
                      onClick={validateDataset}
                      aria-describedby="start-engine-requirement"
                      data-testid="lottery-start-engine"
                      className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      {busy || isCheckingAccess ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      {startEngineLabel}
                    </button>
                    <p id="start-engine-requirement" className="mt-3 text-center text-xs leading-5 text-white/38">
                      {workflowActive
                        ? "The current engine run is still in progress."
                        : sourceMode === "link"
                          ? validateSourceUrl(sourceUrl)
                            ? "The linked document is ready to import."
                            : "Paste a public HTTPS file link before starting the engine."
                          : file
                            ? `${file.name} is ready for analysis.`
                            : "Choose a historical draw document before starting the engine."}
                    </p>
                  </>
                )}
              </div>

              <div className="min-h-[520px] rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-6 sm:p-8">
                {restoringProject ? (
                  <div className="flex min-h-[455px] flex-col items-center justify-center text-center">
                    <LoaderCircle className="h-9 w-9 animate-spin text-sky-200" />
                    <p className="mt-5 text-lg font-semibold">Loading this analysis project</p>
                    <p className="mt-2 max-w-md text-sm leading-6 text-white/50">
                      Retrieving the project state and its bound report.
                    </p>
                  </div>
                ) : !project ? (
                  <div className="flex h-full min-h-[455px] flex-col justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.24em] text-white/40">What happens next</p>
                      <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em]">Verify the data before the model ranks a number.</h2>
                      <p className="mt-5 max-w-xl text-sm leading-7 text-white/52">
                        We check draw dates, six unique main numbers, special-number integrity, duplicate conflicts, sequence gaps, source coverage, and target-date leakage.
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {["100+ valid draws", "Number integrity", "Target-date cutoff"].map((label) => (
                        <div key={label} className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4 text-sm text-white/58">
                          <ShieldCheck className="mb-3 h-4 w-4 text-sky-200/70" />
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : project.status === "validating" ? (
                  <div className="flex min-h-[455px] flex-col items-center justify-center text-center">
                    <LoaderCircle className="h-9 w-9 animate-spin text-sky-200" />
                    <p className="mt-5 text-lg font-semibold">Checking every draw record</p>
                    <p className="mt-2 max-w-md text-sm leading-6 text-white/50">{project.message}</p>
                    <div className="mt-8 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-sky-200 transition-all" style={{ width: `${Math.max(2, project.progress)}%` }} />
                    </div>
                    <p className="mt-3 font-mono text-xs text-white/38">{project.progress}%</p>
                    {project.executionPlan ? (
                      <div className="mt-6 w-full max-w-md rounded-2xl border border-sky-200/10 bg-sky-200/[0.035] p-4 text-left">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-sky-100/45">Prompt interpreted</p>
                        <p className="mt-2 text-xs leading-5 text-white/55">{project.executionPlan.objective}</p>
                        <p className="mt-2 font-mono text-[10px] text-white/35">
                          {project.executionPlan.agents.length} agents · {project.executionPlan.requestedMethods.length} requested methods
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : project.status === "failed" ? (
                  <div className="rounded-[24px] border border-rose-300/20 bg-rose-400/[0.06] p-6">
                    <AlertTriangle className="h-6 w-6 text-rose-200" />
                    <h2 className="mt-4 text-xl font-semibold">Validation or analysis stopped</h2>
                    <p className="mt-3 text-sm leading-6 text-rose-50/70">{project.error || project.message}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-rose-200/45">
                      {project.errorCode || "lottery_workflow_failed"}
                    </p>
                  </div>
                ) : project.status === "awaiting_confirmation" ? (
                  <div className="flex min-h-[455px] flex-col items-center justify-center text-center">
                    {autoRunProjectId === project.projectId ? (
                      <LoaderCircle className="h-9 w-9 animate-spin text-sky-200" />
                    ) : (
                      <Sparkles className="h-9 w-9 text-sky-200" />
                    )}
                    <p className="mt-5 text-lg font-semibold">
                      {autoRunProjectId === project.projectId
                        ? "Starting statistical analysis"
                        : "Analysis is ready to start"}
                    </p>
                    <p className="mt-2 max-w-md text-sm leading-6 text-white/50">
                      {autoRunProjectId === project.projectId
                        ? "The source checks passed. Preparing the statistical models now."
                        : "This saved project paused before the analysis started."}
                    </p>
                    {autoRunProjectId !== project.projectId ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={resumeAnalysis}
                        className="mt-7 inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-black transition hover:bg-white/90 disabled:opacity-45"
                      >
                        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        Run analysis
                      </button>
                    ) : null}
                  </div>
                ) : project.status === "report_ready" ? (
                  <div className="flex min-h-[455px] flex-col items-center justify-center text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-300/12 text-emerald-200">
                      <Check className="h-7 w-7" />
                    </span>
                    <p className="mt-5 text-lg font-semibold">Your statistical report is ready</p>
                    <p className="mt-2 max-w-md text-sm leading-6 text-white/50">{project.message}</p>
                    <div className="mt-8 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-white/10">
                      <div className="h-full w-full rounded-full bg-emerald-200" />
                    </div>
                    <p className="mt-3 font-mono text-xs text-emerald-100/55">100% · report ready</p>
                    <a
                      href="#lottery-analysis-report"
                      className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-black transition hover:bg-white/90"
                    >
                      Read the full report
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                ) : (
                  <div className="flex min-h-[455px] flex-col items-center justify-center text-center">
                    <LoaderCircle className="h-9 w-9 animate-spin text-sky-300" />
                    <p className="mt-5 text-lg font-semibold">Testing historical patterns</p>
                    <p className="mt-2 max-w-md text-sm leading-6 text-white/50">{project.message}</p>
                    <div className="mt-8 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-sky-300 transition-all" style={{ width: `${Math.max(2, project.progress)}%` }} />
                    </div>
                    <p className="mt-3 font-mono text-xs text-white/38">{project.progress}%</p>
                  </div>
                )}
              </div>
            </div>

            {error ? (
              <div role="alert" className="mt-6 flex items-start gap-3 rounded-2xl border border-rose-300/20 bg-rose-400/[0.07] px-5 py-4 text-sm text-rose-50/78">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-200" />
                <span>{error}</span>
              </div>
            ) : null}
          </div>
        </section>

        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:items-end">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-sky-200/55">Method before numbers</p>
                <h2 className="mt-4 max-w-xl text-balance text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                  What this lottery analyzer actually tests
                </h2>
              </div>
              <p className="max-w-2xl text-sm leading-7 text-white/52 sm:text-base">
                A historical pattern is not automatically a prediction. MiroFish validates the source, tests each signal on unseen draws, and falls back to the fair 6/49 baseline when the evidence does not hold up.
              </p>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              {lotteryMethodCards.map((item, index) => (
                <article key={item.title} className="rounded-[28px] border border-white/10 bg-white/[0.04] p-6 sm:p-7">
                  <span className="font-mono text-xs text-sky-200/48">0{index + 1}</span>
                  <h3 className="mt-8 text-xl font-semibold tracking-[-0.025em]">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/50">{item.description}</p>
                </article>
              ))}
            </div>

            <div className="mt-16 grid gap-8 rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(56,189,248,0.07),rgba(255,255,255,0.025),rgba(249,115,22,0.06))] p-6 sm:p-8 lg:grid-cols-[0.72fr_1.28fr] lg:p-10">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/40">Three clear steps</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em]">From uploaded history to an evidence-checked report</h2>
                <p className="mt-5 text-sm leading-7 text-white/50">
                  You stay in control of the dataset and decide when the analysis begins.
                </p>
              </div>
              <ol className="grid gap-4">
                {lotteryWorkflowSteps.map((item) => (
                  <li key={item.step} className="grid gap-4 rounded-2xl border border-white/[0.08] bg-black/15 p-5 sm:grid-cols-[52px_1fr]">
                    <span className="font-mono text-sm text-amber-200/65">{item.step}</span>
                    <div>
                      <h3 className="font-semibold text-white/88">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-white/48">{item.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-20 grid gap-10 lg:grid-cols-[0.72fr_1.28fr]">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/40">Lotto 6/49 FAQ</p>
                <h2 className="mt-4 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">Before you trust a lottery analysis</h2>
                <p className="mt-5 max-w-md text-sm leading-7 text-white/50">
                  The useful question is not whether a pattern exists in the past, but whether it remains reliable on data the model did not use to discover it.
                </p>
                <Link
                  href={WORLD_CUP_PREDICTION_PATH}
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-sky-200/75 transition hover:text-sky-100"
                >
                  Explore the World Cup Prediction lab
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="divide-y divide-white/10 border-y border-white/10">
                {lotteryPredictionFaq.map((item) => (
                  <details key={item.question} className="group py-5 sm:py-6">
                    <summary className="cursor-pointer list-none pr-8 text-base font-semibold text-white/82 marker:hidden">
                      {item.question}
                    </summary>
                    <p className="mt-4 max-w-3xl text-sm leading-7 text-white/50">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {result ? (
          <section id="lottery-analysis-report" className="relative scroll-mt-20 overflow-hidden border-t border-white/10 bg-[#04050a] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute right-[8%] top-0 h-[360px] w-[360px] rounded-full bg-violet-500/10 blur-[130px]" />
              <div className="absolute bottom-[8%] left-[5%] h-[320px] w-[320px] rounded-full bg-sky-500/10 blur-[120px]" />
            </div>
            <div className="relative mx-auto max-w-7xl">
              <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/40">Prompt-controlled report</p>
                  <h2 className="mt-3 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">Your instructions, executed against the draw history.</h2>
                </div>
                <div className={`max-w-lg rounded-2xl border px-5 py-4 text-sm leading-6 ${result.consensus.uniformFallback ? "border-amber-200/20 bg-amber-200/[0.05] text-amber-50/72" : "border-emerald-200/20 bg-emerald-200/[0.05] text-emerald-50/72"}`}>
                  {result.consensus.decision}
                </div>
              </div>

              <div className="mt-10 grid gap-5 lg:grid-cols-3">
                {result.scenarios.map((scenario) => (
                  <article key={scenario.id} className="rounded-[28px] border border-white/10 bg-white/5 p-6">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">{scenario.name}</p>
                    <div className="mt-6 grid grid-cols-6 gap-2">
                      {scenario.combination.map((number) => (
                        <span key={number} className="flex aspect-square items-center justify-center rounded-full border border-white/12 bg-white/[0.07] font-mono text-sm text-white">
                          {String(number).padStart(2, "0")}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Odd/even <span className="mt-1 block font-mono text-white/82">{scenario.structure.oddEven}</span></div>
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Low/high <span className="mt-1 block font-mono text-white/82">{scenario.structure.lowHigh}</span></div>
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Sum <span className="mt-1 block font-mono text-white/82">{scenario.structure.sum}</span></div>
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Span <span className="mt-1 block font-mono text-white/82">{scenario.structure.span}</span></div>
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Mirror pairs <span className="mt-1 block font-mono text-white/82">{scenario.structure.mirrorPairs}</span></div>
                      <div className="rounded-xl bg-white/[0.035] p-3 text-white/52">Largest decade cluster <span className="mt-1 block font-mono text-white/82">{scenario.structure.maxDecadeCluster}</span></div>
                    </div>
                    <p className="mt-5 text-xs leading-5 text-white/44">{scenario.reasoning}</p>
                    <p className="mt-4 font-mono text-[10px] text-white/50">Jackpot odds {scenario.jackpotOdds}</p>
                  </article>
                ))}
              </div>

              {result.executionPlan ? (
                <details className="mt-10 rounded-[28px] border border-white/10 bg-white/[0.035] p-6 open:bg-white/[0.045] sm:p-8">
                  <summary className="cursor-pointer list-none marker:hidden">
                    <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">Prompt execution details</p>
                        <h3 className="mt-2 text-xl font-semibold text-white/88">See what the system actually ran</h3>
                      </div>
                      <div className="font-mono text-[10px] text-white/35">
                        {result.executionPlan.agents.length} agents · {result.executedMethods?.length || 0} methods · plan {result.planHash?.slice(0, 12)}
                      </div>
                    </div>
                  </summary>
                  <div className="mt-7 grid gap-6 lg:grid-cols-3">
                    <div>
                      <p className="text-xs font-semibold text-white/72">Effective analysis cutoff</p>
                      <p className="mt-2 font-mono text-sm text-sky-100/75">{result.executionPlan.effectiveTargetDate}</p>
                      <p className="mt-3 text-xs leading-5 text-white/42">{result.executionPlan.objective}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white/72">Executed methods</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(result.executedMethods || []).map((method) => (
                          <span key={method} className="rounded-full border border-white/10 px-2.5 py-1.5 text-[10px] text-white/48">{method}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white/72">Reasoning agents</p>
                      <div className="mt-3 space-y-2">
                        {result.executionPlan.agents.map((agent) => (
                          <div key={agent.id} className="rounded-xl border border-white/[0.07] px-3 py-2">
                            <p className="text-xs text-white/65">{agent.name}</p>
                            <p className="mt-1 text-[10px] leading-4 text-white/35">{agent.role}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {result.resolvedMethodParameters &&
                  Object.keys(result.resolvedMethodParameters).length ? (
                    <div className="mt-7 border-t border-white/[0.08] pt-5">
                      <p className="text-xs font-semibold text-white/72">Resolved parameters</p>
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {Object.entries(result.resolvedMethodParameters).map(([key, value]) => (
                          <div key={key} className="rounded-xl bg-black/20 px-3 py-2.5">
                            <p className="font-mono text-[10px] text-white/35">{key}</p>
                            <p className="mt-1 break-words font-mono text-[10px] leading-5 text-sky-100/58">
                              {formatExecutionParameter(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {result.executionLedger?.length ? (
                    <div className="mt-7 border-t border-white/[0.08] pt-5">
                      <p className="text-xs font-semibold text-white/72">Execution ledger</p>
                      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {result.executionLedger.map((entry) => (
                          <div key={entry.taskId} className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2 text-[10px]">
                            <span className="truncate text-white/48">{entry.title || entry.taskId}</span>
                            <span className="shrink-0 font-mono text-emerald-200/55">{entry.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </details>
              ) : null}

              <div className="mt-10 grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
                <MiroFishGraphPanel graph={result.graph} headline="49-number temporal relationship graph" />
                <div className="rounded-[30px] border border-white/10 bg-white/5 p-6">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                    {result.executionPlan
                      ? "Prompt-controlled number ranking"
                      : result.consensus.uniformFallback
                        ? "Descriptive top 20 · probability tied"
                        : "Calibrated top 20 ranking"}
                  </p>
                  <div className="mt-5 space-y-2">
                    {result.top20.map((item) => (
                      <div key={item.number} className="grid grid-cols-[34px_44px_1fr_auto] items-center gap-3 rounded-xl border border-white/[0.06] px-3 py-2.5 text-xs">
                        <span className="font-mono text-white/28">#{item.rank}</span>
                        <span className="font-mono text-sm text-sky-100">{String(item.number).padStart(2, "0")}</span>
                        <span className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                          <span className="block h-full rounded-full bg-sky-300/60" style={{ width: `${Math.min(100, Math.max(4, item.probabilityPercent * 6))}%` }} />
                        </span>
                        <span className="font-mono text-white/48">{item.probabilityPercent.toFixed(4)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10 grid gap-8 lg:grid-cols-[0.7fr_1.3fr]">
                <div className="rounded-[28px] border border-white/10 bg-white/5 p-6">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                    {result.executionPlan ? "Prompt-requested lower ranking" : "Low descriptive-score group"}
                  </p>
                  <p className="mt-3 text-xs leading-5 text-white/44">{result.bottom20Label}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {result.bottom20.map((item) => (
                      <span key={item.number} className="rounded-full border border-white/10 px-3 py-2 font-mono text-xs text-white/55">
                        {String(item.number).padStart(2, "0")}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 sm:p-8">
                  <div className="flex items-center justify-between gap-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">Structured report</p>
                      <h3 className="mt-2 text-2xl font-semibold">Method, evidence, and uncertainty</h3>
                    </div>
                    <ArrowRight className="h-5 w-5 text-sky-200/50" />
                  </div>
                  <p className="mt-5 text-sm leading-7 text-white/58">{result.explanation}</p>
                  <details className="mt-6 rounded-2xl border border-white/[0.08] bg-black/15 p-4">
                    <summary className="cursor-pointer text-sm font-semibold text-white/70">Read the full methodology and report</summary>
                    <pre className="mt-5 max-h-[640px] overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-6 text-white/52">{result.reportMarkdown}</pre>
                  </details>
                </div>
              </div>

              <div className="mt-10 flex items-start gap-3 rounded-2xl border border-amber-200/15 bg-amber-200/[0.035] p-5 text-sm leading-6 text-amber-50/62">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
                <p>{result.responsibleUse} Every exact six-number set retains probability 1/13,983,816.</p>
              </div>
            </div>
          </section>
        ) : null}
      </main>
      <MiroFishSiteFooter />
    </div>
  );
}
